<template>
  <div class="padding">
    <div v-if="loading">
      <QuizJoinGameForm v-if="isAudienceFormShowing" @joinGame="handleJoinGame" />

      <div
        v-if="(!$store.state.presentation.audiencePacing &&
          $store.state.presentation.presenting || $store.state.presentation.audiencePacing) &&
          !isAudienceFormShowing"
      >
        <transition name="slide-fade">
          <div
            v-if="slideActive.quizStatus == QuizStatuses.LOBBY_SCREEN && !hasResult"
            class="text-center"
          >
            <p
              class="lobby-audience-name"
            >
              {{ $store.state.audienceEmoji }}
              <span
                class="font-weight-bold"
                :style="{ color: getAudienceColor }"
              >
                {{ $store.state.audienceName }}
              </span>
            </p>

            <div class="mb-3.5">
              <div class="lobby-title">
                <strong
                  class="mb-2 layout center horizontal center-justified animate__pulse animate__animated animate__infinite"
                >
                  {{ $t("getReady", { msg: '' }) }}
                </strong>
              </div>
              <div v-if="$store.state.presentation.teamPlay">
                <span
                  class="mr-1"
                  v-html="$t('youAreIn', { msg: `<strong>${chosenTeam.name}</strong>` })"
                />
                <span v-if="editTeamQuiz">
                  <span>(</span>
                  <span
                    @click="changeQuizTeam()"
                    class="change-team d-inline-block"
                  >
                    {{ $t('changeTeam') }}
                  </span>
                  <span>)</span>
                </span>
              </div>
            </div>

            <div class="mb-5">
              {{ $t('questionWillStartSoon') }}
            </div>
          </div>
        </transition>
        <transition name="slide-fade">
          <div v-if="slideActive.quizStatus == QuizStatuses.RULE_SCREEN && !hasResult" class="text-center">
            <QuizCounter />

            <p
              v-if="slideActive.fastAnswerGetMorePoint"
              class="mb-3 note-point"
            >
              {{ $t("fasterGetMorePointsRule") }}
            </p>
            <p
              v-if="!slideActive.fastAnswerGetMorePoint"
              class="mb-3 note-point"
            >
              {{ $t("correctAnswerGetMaxPointTitle") }}
            </p>

            <div class="icon-step-2">
              <i
                class="fas fa-stopwatch fa-3x animate__animated animate__tada animate__delay-1s"
                v-if="slideActive.fastAnswerGetMorePoint"
              />
            </div>
            <p
              v-if="!slideActive.fastAnswerGetMorePoint"
              class="mb-3 small-note-point"
            >
              {{ $t("correctAnswerGetMaxPointDetail") }}
            </p>
          </div>
        </transition>
        <div>
          <div
            class="text-center step-4"
            v-if="canShowQuestion"
          >
            <h2
              class="mt-0 text-left slide-title-style"
              v-html="dataValue"
            />
            <p
              class="mb-3 text-left title-desc"
              v-if="slideActive.titleDesc"
            >
              {{ slideActive.titleDesc }}
            </p>

            <div
              class="audio-control-slide"
              v-if="
                slideActive.audioLink
                  && slideActive.audioPlayOnAudience
                  && slideActive.id
              "
            >
              <AudioControl />
            </div>
          </div>
          <transition name="slide-fade">
            <div v-if="slideActive.quizStatus == QuizStatuses.COUNTDOWN_5S_SCREEN && !hasResult" class="text-center step-4">
              <div
                v-if="slideActive.image"
                class="countdown-has-image"
                :class="{'has-hints': slideActive.isHintsVisible && !presentation.audiencePacing}"
              >
                <img :src="slideActive.image.large" alt>
              </div>

              <div id="countdown" :class="{'countdown-bg': !slideActive.image}">
                <div id="countdown-number" />
                <svg v-show="!slideActive.image">
                  <circle r="107" cx="120" cy="120" />
                </svg>
              </div>
            </div>
          </transition>
          <transition name="slide-fade">
            <div
              v-if="canShowImageAndHints"
              class="text-center step-4"
            >
              <QuestionImageAndHints />
              <div
                class="progress-bar-time-answer-count progress-bar-v2"
              >
                <div
                  class="max-point"
                  v-if="slideActive.fastAnswerGetMorePoint"
                >
                  {{ slideActive.maxPoint }}{{ $t("pointShortcut") }}
                </div>
                <div
                  class="min-point"
                  v-if="slideActive.fastAnswerGetMorePoint"
                >
                  {{ slideActive.minPoint }}{{ $t("pointShortcut") }}
                </div>

                <ProgressBarQuizV2 :result="result" />
              </div>
              <QuizForm ref="quiz" :result="result" />
            </div>
          </transition>

          <div v-if="hasResult && !$store.state.presentation.audiencePacing">
            <AlreadyAnswer
              v-if="
                slideActive.quizStatus == QuizStatuses.LOBBY_SCREEN
                  || slideActive.quizStatus == QuizStatuses.RULE_SCREEN
                  || slideActive.quizStatus == QuizStatuses.COUNTDOWN_5S_SCREEN
                  || (slideActive.quizStatus == QuizStatuses.QUESTION_SCREEN && isAlreadyAnswered)
              "
            />
          </div>
        </div>
      </div>
      <div
        class="text-center"
        v-if="canShowQuizNotOpenText"
      >
        {{ $t("quizIsNotOpen") }}
        <a
          class="link-reload"
          @click="reloadPage()"
          :class="{'white': textColour == '#ffffff'}"
        >{{ $t("reloadTextLink") }}</a>
      </div>
    </div>
    <div v-if="!loading" class="text-center padding">
      <Loading style="margin: 0 auto;" />
    </div>
  </div>
</template>
<script>
import * as Sentry from '@sentry/vue'

// components
import QuizForm from '@/components/quiz/QuizForm.vue'
import QuizCounter from '@/components/common/QuizCounter.vue'
import QuestionImageAndHints from '@/components/pickAnswer/QuestionImageAndHints'

// helpers
import colorUtils from 'storybook-utils/colors'

// containers
import QuizCounterContainer from '@/containers/common/QuizCounter'

import data from '@/assets/data/all.json'

import Config from '@/config'
import { EmojiIndex } from 'emoji-mart-vue-fast'
import Util from '@/util'
import Loading from '@/components/Loading'
import EventBus from '@/eventBus'
import ProgressBarQuizV2 from '@/components/ProgressBarQuizV2.vue'
import AudioControl from '@/components/AudioControl.vue'
import QuizJoinGameForm from '@/components/QuizJoinGameForm.vue'
import AlreadyAnswer from '@/components/quiz/AlreadyAnswer.vue'
import storage from '@/services/storage'
import { mapState, mapActions, mapGetters } from 'vuex'
import QuizStatuses from '@/constants/QuizStatuses'
import ClickableTitleMixin from '@/mixins/ClickableTitleMixin'

import AnswersAPI from '@/api/answers'

export default {
  components: {
    AlreadyAnswer,
    Loading,
    QuizForm,
    ProgressBarQuizV2,
    AudioControl,
    QuizJoinGameForm,
    QuizCounter: QuizCounterContainer(QuizCounter),
    QuestionImageAndHints,
  },
  mixins: [ClickableTitleMixin],
  data() {
    return {
      openEmoji: false,
      submiting: false,
      loading: false,
      result: {},
      timeAnswer: 0,
      interval: null,
      timeLeft: 0,
      error: false,
      audienceName: this.$store.state.audienceName,
      intervalCountdownCycle: null,
      emojiIndex: null,
      requestFrame: null,
      editTeamQuiz: false,
      isAlreadyAnswered: false,
    }
  },

  computed: {
    ...mapState({
      quizStatus: (_, getters) => getters.activeActivity.quizStatus,
      audienceId: (state) => state.audienceId,
      presentation: (state) => state.presentation,
      textColour: (_, getters) => getters.activeActivity.textColour,
      chosenTeam: (state) => {
        return state.presentation.teamData ?
          state.presentation.teamData.find((team) => team.id === state.audienceQuizTeam) : {}
      },
    }),
    ...mapGetters({
      slideActive: 'activeActivity',
    }),
    hasResult() {
      return this.result && this.result.id
    },
    isAudienceFormShowing() {
      if (this.$store.state.presentation.teamPlay) {
        return (
          !this.$store.state.audienceName ||
          !this.$store.state.isAudienceEmojiDisabled ||
          !this.$store.state.audienceQuizTeam
        )
      }
      return (
        !this.$store.state.audienceName ||
        !this.$store.state.isAudienceEmojiDisabled
      )
    },

    canShowQuestion() {
      return (this.slideActive.quizStatus == QuizStatuses.COUNTDOWN_5S_SCREEN && !this.isAlreadyAnswered)
              || (this.slideActive.quizStatus == QuizStatuses.QUESTION_SCREEN && !this.isAlreadyAnswered)
              || this.slideActive.quizStatus == QuizStatuses.RESULT_SCREEN
    },

    canShowImageAndHints() {
      return (this.slideActive.quizStatus == QuizStatuses.QUESTION_SCREEN && !this.isAlreadyAnswered) ||
        this.slideActive.quizStatus == QuizStatuses.RESULT_SCREEN
    },

    canShowQuizNotOpenText() {
      return !this.presentation.audiencePacing && !this.presentation.presenting && !this.isAudienceFormShowing
    },

    getAudienceColor() {
      return colorUtils.getAudienceColor(this.audienceId)
    },
  },
  watch: {
    '$store.state.presentation.presenting': function handlePresentingChange() {
      if (!this.$store.state.presentation.presenting) {
        if (this.interval) {
          clearInterval(this.interval)
          this.interval = null
        }
      }
    },
    'slideActive.quizStatus': function handleQuizStatusChange() {
      if (this.slideActive.quizStatus === QuizStatuses.LOBBY_SCREEN) {
        if (this.requestFrame) {
          cancelAnimationFrame(this.requestFrame)
          this.requestFrame = null
        }
      }

      if (this.slideActive.quizStatus === QuizStatuses.RESULT_SCREEN) {
        if (this.isAlreadyAnswered) {
          setTimeout(() => {
            EventBus.$emit('show-full-progress-bar-quiz')
          })
        } else {
          this.isAlreadyAnswered = true
        }
      }
    },
  },
  created() {
    this.QuizStatuses = QuizStatuses
  },
  mounted() {
    const self = this
    this.emojiIndex = new EmojiIndex(data)

    EventBus.$on('quizGetResult', () => {
      if (!this.hasResult) {
        this.result = {
          correct: false,
          timeout: true,
          notPlay: true,
        }
      }

      EventBus.$emit('show-full-progress-bar-quiz')
    })

    if (this.$store.state.audienceName) {
      if (!this.$store.state.presentation.teamPlay || this.$store.state.audienceQuizTeam) {
        try {
          const slideId = this.slideActive.id
          const presentationId = this.$store.state.presentation.id
          const answeredSlideIds = storage.getValueByPresentation(presentationId, 'answeredSlideIds', [])
          if (answeredSlideIds.includes(slideId)) {
            this.isAlreadyAnswered = true

            this.getResult()
            EventBus.$emit('show-full-progress-bar-quiz')
          } else {
            this.isAlreadyAnswered = false
            this.result = {
              id: null,
              status: 4,
            }
            this.loading = true
            if (this.slideActive.quizStatus === QuizStatuses.LOBBY_SCREEN) {
              this._updateAudienceAnswer(false)
            }

            if (this.slideActive.quizStatus === QuizStatuses.RESULT_SCREEN) {
              this.result = {
                correct: false,
                timeout: true,
                notPlay: true,
              }

              EventBus.$emit('show-full-progress-bar-quiz')
            }
          }
          if (
            answeredSlideIds
            && !answeredSlideIds.length
            && this.$store.state.presentation.teamPlay
          ) {
            this.editTeamQuiz = true
          }
        } catch (error) {
          Sentry.captureException(error)
          this.getResult()
        }
        this.reCountDown()
        this._startCountDownCycle()
      } else {
        this.loading = true
      }
    } else {
      this.loading = true
    }

    EventBus.$on('start-countdown-pick-answer', () => {
      self._startCountDownTimeToAnswer()
    })

    EventBus.$on('quiz-waiting-result', (payload) => {
      self.timeAnswer = payload.timeAnswer
      self.timeLeft = payload.timeLeft
      self.result = payload.answer
      if (!payload.notSubmit) {
        self._updateAudienceAnswer(true)
      }
      self.loading = true
    })

    EventBus.$on('quiz-stop-submit', () => {
      clearInterval(self.interval)
      self.interval = null
    })

    EventBus.$on('cancelAnimationFrame', () => {
      // cancel đi để tránh việc submit answer rồi mà vẫn chạy -> báo time up
      cancelAnimationFrame(self.requestFrame)
    })

    EventBus.$on('startQuizScreenTwo', () => {
      this._startCountDownCycle()
    })

    EventBus.$on('quizTimeout', () => {
      if (this.$store.state.audienceName && !this.hasResult
      ) {
        this._createTimeupAnswer()
      }
    })

    EventBus.$on('recheck-progress-bar', () => {
      this.reCountDown(true)
    })

    EventBus.$on('acceptTypeAnswer', (data) => {
      if (this.slideActive.id == data.slideId
        && data.audiences
        && data.audiences.includes(this.audienceId)
      ) {
        window.location.reload()
      }
    })

    this.handleCacheImage()
  },
  destroyed() {
    EventBus.$off('start-countdown-pick-answer')
    EventBus.$off('quiz-stop-submit')
    EventBus.$off('startQuizScreenTwo')
    EventBus.$off('quiz-waiting-result')
    EventBus.$off('quizTimeout')
    EventBus.$off('quizGetResult')
    EventBus.$off('acceptTypeAnswer')
    EventBus.$off('recheck-progress-bar')
    EventBus.$off('cancelAnimationFrame')

    if (this.interval) {
      clearInterval(this.interval)
      this.interval = null
    }

    if (this.requestFrame) {
      cancelAnimationFrame(this.requestFrame)
      this.requestFrame = null
    }
  },
  methods: {
    ...mapActions(['setConnectionError']),
    async changeQuizTeam() {
      storage.updateValueByPresentation(this.presentation.id, 'audienceId', this.audienceId)
      // eslint-disable-next-line promise/catch-or-return
      this.$http.post(Config.api.leaveQuizTeam, {
        c: this.$store.state.presentation.uniqueAccessCode,
        a: this.audienceId,
        t: this.$store.state.audienceQuizTeam,
      }).then(() => {}, () => {})
      this.$store.state.audienceQuizTeam = null
      storage.setValueByPresentation(this.$store.state.presentation.id, 'audienceQuizTeam', null)

      Util.mixpanelTrack(
        'Quiz Team',
        'Audience change team',
        'Audience change team',
      )
    },
    async _createTimeupAnswer() {
      if (!this.$store.state.audienceName || this.hasResult) return

      if (this.$store.state.isSlideAnswerSubmitting[this.slideActive.id]) {
        // Kiểm tra xem có đang submit quiz nào ko, nếu có thì sẽ ko tạo timeupAnswer nữa
        return
      }

      const {
        id: presentationId,
        uniqueAccessCode,
      } = this.$store.state.presentation

      this.result = {
        slide: 5214,
        vote: [],
        timeout: true,
        status: 3,
        typeCorrectAnswer: 1,
        point: 0,
        id: 1,
      }

      storage.updateValueByPresentation(this.presentation.id, 'audienceId', this.audienceId)
      const payload = {
        presentation: presentationId,
        presentationId,
        slide: this.slideActive.id,
        slideId: this.slideActive.id,
        type: this.slideActive.type,
        audience: this.audienceId,
        accessCode: uniqueAccessCode,
        vote: [],
        categories: [],
        timeAnswer: this.slideActive.timeToAnswer,
        audienceName: this.$store.state.audienceName,
        audienceEmoji: this.$store.state.audienceEmoji,
        point: 0,
        config: {
          timeToAnswer: this.slideActive.timeToAnswer,
          quizTimestamp: this.slideActive.quizTimestamp,
          multipleChoice: this.slideActive.multipleChoice,
          isCorrectGetPoint: this.slideActive.isCorrectGetPoint,
          stopSubmission: this.slideActive.stopSubmission,
          fastAnswerGetMorePoint: this.slideActive.fastAnswerGetMorePoint,
          otherCorrectQuiz: this.slideActive.otherCorrectQuiz,
          correctQuizTypeAnswer: this.slideActive.correctQuizTypeAnswer,
          quizToken: this.slideActive.quizToken,
        },
      }

      if (this.$store.state.presentation.teamPlay) {
        payload.audienceTeam = this.$store.state.audienceQuizTeam
      }

      try {
        if (!payload.slide) return
        const res = this.slideActive.slideType === 'categorise' ?
          await AnswersAPI.createCategoriseAnswer(payload) :
          await this.$http.post(Config.api.createAnswer, payload)

        this.result = res.body

        const answeredSlideIds = storage.getValueByPresentation(presentationId, 'answeredSlideIds', [])

        answeredSlideIds.push(this.slideActive.id)
        storage.setValueByPresentation(presentationId, 'answeredSlideIds', answeredSlideIds)
      } catch (error) {
        if (Sentry && error.status) {
          Sentry.captureException(JSON.stringify(error))
        }
      }
    },

    handleCacheImage() {
      const arr = []

      if (this.slideActive.image) {
        arr.push(this.slideActive.image.large)
      }

      this.slideActive.SlideOptions.forEach((item) => {
        if (item.imageCropped) {
          arr.push(item.imageCropped)
        }
        if (item.pairImageCropped) {
          arr.push(item.pairImageCropped)
        }
      })

      const preloadImages = (array) => {
        if (!preloadImages.list) {
          preloadImages.list = []
        }
        const { list } = preloadImages
        for (let i = 0; i < array.length; i++) {
          const img = new Image()
          img.onload = () => {
            const index = list.indexOf(this)
            if (index !== -1) {
              // remove image from the array once it's loaded
              // for memory consumption reasons
              list.splice(index, 1)
            }
          }
          list.push(img)
          img.src = array[i]
        }
      }

      preloadImages(arr)
    },
    clickOutSideEmoji() {
      if (this.openEmoji) {
        this.openEmoji = false
      }
    },
    _startCountDownCycle() {
      setTimeout(() => {
        if (this.intervalCountdownCycle || this.slideActive.quizStatus !== 3) return
        this.intervalCountdownCycle = true
        const countdownNumberEl = document.getElementById('countdown-number')
        if (!countdownNumberEl) return
        let countdown = 5
        countdownNumberEl.textContent = countdown
        const intervalCountdownCycle = setInterval(() => {
          countdown--
          countdownNumberEl.textContent = countdown
          if (!countdown || countdown < 1) {
            window.clearInterval(intervalCountdownCycle)
            this.intervalCountdownCycle = null
          }
        }, 1000)
      }, 200)
    },
    async reCountDown() {
      if (this.slideActive.quizStatus === QuizStatuses.RESULT_SCREEN) {
        this.$store.state.countdownTimeToAnswer = 0
        // this._startCountDownTimeToAnswer()
      }
      if (this.slideActive.quizStatus === QuizStatuses.QUESTION_SCREEN) {
        const startCallApiGetTimeStamp = Date.now()

        try {
          const res = await this.$http.get(Config.api.getTimestamp)
          const endCallApiGetTimeStamp = Date.now()
          if (this.slideActive.quizTimestamp && this.slideActive.quizTimestamp.length) {
            let currentTime =
              (parseInt(
                this.slideActive.quizTimestamp[
                  this.slideActive.quizTimestamp.length - 1
                ],
                10,
              ) +
                this.slideActive.timeToAnswer * 1000 -
                  (res.body.timeStamp + (endCallApiGetTimeStamp - startCallApiGetTimeStamp))) /
              1000
            if (currentTime > 0) {
              if (currentTime > this.slideActive.timeToAnswer) {
                currentTime = this.slideActive.timeToAnswer
              }
              // giảm 0.5s để progressbar chay đồng bộ với trường hợp không f5
              currentTime -= 0.5
              this.$store.state.countdownTimeToAnswer = currentTime
              this._startCountDownTimeToAnswer(true)
              EventBus.$emit('restart-progress-bar-quiz')
            } else {
              this.$store.state.countdownTimeToAnswer = 0
              this._startCountDownTimeToAnswer(true)
            }
          }
        } catch (error) {
          Sentry.captureException(error)
        }
      }
    },
    getTimeCount(second) {
      return Util.getTimeCount(second, this)
    },
    reloadPage() {
      window.location.reload()
    },
    onChangeAudienceName() {
      this.error = false
    },
    async getResult() {
      storage.updateValueByPresentation(this.presentation.id, 'audienceId', this.audienceId)
      const payload = {
        slideId: this.slideActive.id,
        audience: this.audienceId,
        presentationId: this.$store.state.presentation.id,
        config: {
          timeToAnswer: this.slideActive.timeToAnswer,
          quizTimestamp: this.slideActive.quizTimestamp,
          multipleChoice: this.slideActive.multipleChoice,
          isCorrectGetPoint: this.slideActive.isCorrectGetPoint,
          stopSubmission: this.slideActive.stopSubmission,
          fastAnswerGetMorePoint: this.slideActive
            .fastAnswerGetMorePoint,
          otherCorrectQuiz: this.slideActive.otherCorrectQuiz,
          correctQuizTypeAnswer: this.slideActive.correctQuizTypeAnswer,
        },
        type: this.slideActive.type,
        slideType: this.slideActive.slideType,
      }
      if (this.$store.state.presentation.teamPlay) {
        payload.teamPlay = true
        payload.teamScoringRule = this.$store.state.presentation.teamScoringRule
        payload.teamId = this.$store.state.audienceQuizTeam
      }
      const optionList = []
      this.slideActive.SlideOptions.forEach((dataOption) => {
        optionList.push({
          id: dataOption.id,
          correct: dataOption.correct,
        })
      })
      payload.config.SlideOptions = optionList
      this.$http.post(Config.api.quizResult, payload).then(
        (res) => {
          if (res.body.id) {
            this._updateAudienceAnswer(true)
          } else {
            this.isAlreadyAnswered = false
            this._updateAudienceAnswer(false)
            if (this.slideActive.quizStatus === QuizStatuses.RESULT_SCREEN) {
              res.body.timeout = true
            }
          }
          this.result = res.body
          this.loading = true
        },
        () => {
          this.setConnectionError(true)
        },
      )
    },

    async _updateAudienceAnswer(answer) {
      storage.updateValueByPresentation(this.presentation.id, 'audienceId', this.audienceId)
      this.$http
        .post(Config.api.joinPresentation, {
          a: this.audienceId,
          n: this.$store.state.audienceName,
          e: this.$store.state.audienceEmoji,
          c: this.$store.state.presentation.uniqueAccessCode,
          q: true,
          i: this.slideActive.id,
          t: this.$store.state.audienceQuizTeam,
          r: answer,
        })
        .then(
          () => {},
          () => {},
        )
    },
    _startCountDownTimeToAnswer(resetRequestFrame = false) {
      if (resetRequestFrame) {
        cancelAnimationFrame(this.requestFrame)
      }
      const self = this
      let countTimeToAnswer = 0
      let secondTime = 0
      const curTime = Util.getTimeStamp()
      const startTime =
        curTime + self.$store.state.countdownTimeToAnswer * 1000

      countTimeToAnswer = parseInt(
        (startTime - curTime) / 1000,
        10,
      )

      function step(timestamp) {
        const timeRun = parseInt(timestamp / 1000, 10)
        if (secondTime !== timeRun) {
          secondTime = timeRun
          countTimeToAnswer = parseInt(
            (startTime - Util.getTimeStamp()) / 1000,
            10,
          )
          self.$store.state.countdownTimeToAnswer--

          if (countTimeToAnswer === 0) {
            setTimeout(() => {
              if (!self.hasResult) {
                self.result.timeout = true
                self._createTimeupAnswer()
              }
            }, 1000)
          }
        }

        if (countTimeToAnswer > 0 && !self.hasResult) {
          self.requestFrame = window.requestAnimationFrame(step)
        } else {
          self.requestFrame = null
        }
      }

      self.requestFrame = window.requestAnimationFrame(step)
    },

    handleJoinGame() {
      this.reCountDown()
      this._startCountDownCycle()
      if (this.$store.state.presentation.teamPlay) {
        this.editTeamQuiz = true
      }
    },

    addEmoji(emoji) {
      this.$store.state.audienceEmoji = emoji.native
      this.openEmoji = false
    },
    openPickEmoji() {
      this.openEmoji = !this.openEmoji
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";
.audio-control-slide {
  margin-bottom: 16px;
}
#countdown.countdown-bg {
  background-color: rgba(240, 240, 240, 0.6);
  border-radius: 100%;
}

.icon-step-2 {
  margin-bottom: 16px;
  .fa-stopwatch {
    font-size: 2.5em;
  }
  .fa-ban {
    color: $primary-color;

    &.white {
      color: #fff;
      opacity: 0.5;
    }
  }
}

.button-submit {
  .loading-spinner {
    margin-bottom: 0px;
  }
}

.icon-correct {
  background-color: #18b518;
  width: 50px;
  height: 50px;
  color: #fff;
  border-radius: 50%;
  line-height: 50px;
  font-size: 24px;
  border: 2px solid #fff;
  position: absolute;
  bottom: -27px;
  left: 50%;
  margin-left: -25px;

  &.error {
    background-color: red;
  }
}
.progress-bar-animation {
  background-color: #fff;
  border: 1px solid #fff;
  height: 24px;
  width: 50%;
  margin: 20px auto;
  margin-bottom: 0;
  overflow: hidden;
  .line-bar {
    height: 24px;
    width: 100%;
    background-color: $primary-color;
    animation: progress-bar 5s;
    animation-timing-function: linear;
  }
}

/* Safari 4.0 - 8.0 */
@-webkit-keyframes progress-bar {
  0% {
    width: 0%;
  }
  100% {
    width: 100%;
  }
}

/* Standard syntax */
@keyframes progress-bar {
  0% {
    width: 0%;
  }
  100% {
    width: 100%;
  }
}

.text-action {
  color: $primary-color;

  &.white {
    color: #fff;
    text-decoration: underline;
  }
}

.lobby-audience-name {
  margin-bottom: 16px;
  font-size: 20px;
}

.lobby-title {
  font-size: 24px;
  font-weight: bold;
}

.bubble {
  width: 140px;
  height: 140px;
  background-color: $primary-color;
  border: 1px solid #fff;
  border-radius: 50%;
  line-height: 140px;
  font-size: 50px;
  margin: 0 auto;
  margin-bottom: 32px;
  position: relative;
  margin-top: 8px;
  span {
    display: block;
    animation-iteration-count: infinite;
  }
}
.link-reload {
  margin: 16px 0;
  display: block;
  cursor: pointer;
}
.result {
  font-weight: bold;
  font-size: 20px;
  margin-bottom: 16px;
}

.des-small {
  opacity: 0.8;
}
@-webkit-keyframes leaves {
  0% {
    -webkit-transform: scale(1);
  }
  100% {
    -webkit-transform: scale(1.2);
  }
}

@keyframes leaves {
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(1.2);
  }
}
.pick-emoji {
  position: relative;
  cursor: pointer;
  width: fit-content;
  -webkit-tap-highlight-color: transparent;

  .emoji-mart {
    position: absolute;
    top: 90px;
    left: 0;
    z-index: 110;
  }
}

.emoji-picked {
  font-size: 80px;
  margin: 16px;
}

.no-fast-answer {
  margin-bottom: 8px;
}

.item-input {
  position: relative;
  margin-bottom: 16px;
}

.slide-fade-enter-active {
  transition: all 0.3s ease;
}
// .slide-fade-leave-active {
//   transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
// }
.slide-fade-enter
/* .slide-fade-leave-active below version 2.1.8 */ {
  transform: translateX(-50px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(50px);
  opacity: 0;
}

.step-4 {
  img {
    max-width: 100%;
    margin: 0 auto;
  }

  h2 {
    word-break: break-word;
    word-wrap: normal;
  }
}

.full-bar {
  background-color: #f1f1f1;
  border: 1px solid #fff;
}

.bar-new-1 {
  background: #f1f1f1;
  height: 12px;
  width: 100%;
}

.bar-new-2 {
  animation-name: progress-bar;
  animation-timing-function: linear;
  background: #4f9af2;
  box-sizing: border-box;
  height: 100%;
}

@keyframes progress-bar {
  from {
    width: 0%;
  }
  to {
    width: 100%;
  }
}

.progress-bar-time-answer-count {
  position: relative;
  margin-bottom: 16px;
  padding-top: 20px;
  border: 1px solid transparent;

  .bar-1 {
    height: 10px;
    background-color: #f1f1f1;
    border: 1px solid #fff;
    overflow: hidden;
  }

  .bar-2 {
    height: 10px;
    background-color: $primary-color;
    border: 1px solid #fff;
    width: 0%;
    position: absolute;
    bottom: 0;
    left: 0;
    transition-timing-function: ease-in;
    /* Quick on the way out */
    transition: 0.2s;
  }

  .max-point {
    position: absolute;
    left: 0;
    top: -2px;
    font-weight: bold;
  }

  .min-point {
    position: absolute;
    right: 0;
    top: -2px;
    font-weight: bold;
  }

  &.progress-bar-v2 {
    .max-point {
      right: 0;
      left: initial;
    }

    .min-point {
      left: 0;
      right: initial;
    }
  }
}

.link-reload {
  &.white {
    text-decoration: underline;
    color: #fff;
  }
}

.slide-img-wrapper {
  width: auto;
  margin: 0 auto;
  overflow: hidden;
  padding-bottom: 0;
  display: block;

  .slide-img {
    margin-bottom: 16px;
  }
}
.countdown-has-image {
  img {
    max-width: 100%;
    display: block;
    max-height: 250px;
  }
  &.has-hints {
    background-color: #ffffff;
    width: max-content;
    margin: 0 auto;
  }
}

.change-team {
    text-decoration: underline;
    cursor: pointer;
    color: $primary-color;
}
</style>
